import React, { useState, useEffect } from 'react';
import { useProgress } from './hooks/useProgress';
import Onboarding from './components/Onboarding';
import EveningPrep from './components/EveningPrep';
import DayScreen from './components/DayScreen';
import ResetOverlay from './components/ResetOverlay';
import Footer from './components/Footer';
import FinalModal from './components/FinalModal';
import content from './data/content.json';

const App = () => {
  const {
    progress,
    canStartPractice,
    isDayComplete,
    canGoToNextDay,
    getNextDayStatus,
    getDaysUntilRestart,
    completeOnboarding,
    activatePractice,
    incrementL1,
    toggleL2,
    markInjectionRead,
    goToDay,
    incrementResets,
    restartCourse,
    } = useProgress();

  const [isResetOverlayOpen, setIsResetOverlayOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showFinalModal, setShowFinalModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      const loader = document.getElementById('app-loader');
      if (loader) {
        loader.classList.add('hidden');
        setTimeout(() => loader.remove(), 500);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (progress.status === 'waiting_for_morning' && canStartPractice()) {
      activatePractice();
    }
  }, [progress.status, canStartPractice, activatePractice]);

  useEffect(() => {
    if (progress.currentDay === 7 && isDayComplete(7)) {
      setShowFinalModal(true);
    } else {
      setShowFinalModal(false);
    }
  }, [progress.currentDay, progress.days, isDayComplete]);

  const currentDayData = content.days.find(d => d.id === progress.currentDay);
  const currentDayProgress = progress.days[progress.currentDay];
  const nextDayStatus = getNextDayStatus(progress.currentDay);
  const dayComplete = isDayComplete(progress.currentDay);
  const daysUntilRestart = getDaysUntilRestart();

  const handleOnboardingComplete = () => {
    completeOnboarding();
  };

  const handleResetClick = () => {
    setIsResetOverlayOpen(true);
  };

  const handleResetComplete = () => {
    incrementResets();
    setIsResetOverlayOpen(false);
  };

  const handleResetClose = () => {
    setIsResetOverlayOpen(false);
  };

  const handleNextDay = () => {
    if (canGoToNextDay(progress.currentDay)) {
      goToDay(progress.currentDay + 1);
    }
  };

  const handleDaySelect = (dayNum) => {
    if (dayNum <= progress.maxUnlockedDay) {
      goToDay(dayNum);
    }
  };

  const handleRestart = () => {
    restartCourse();
    setShowFinalModal(false);
  };

  if (isLoading) {
    return null;
  }

  if (!progress.onboardingComplete) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  if (progress.status === 'waiting_for_morning' && !canStartPractice()) {
    return <EveningPrep onComplete={() => {}} isWaiting={true} />;
  }

  const finalMessage = "Настройка проведена. Процесс продолжается. До конца дня пробуй и наблюдай, как эта настройка встраивается в жизнь.";

  return (
    <div style={styles.container}>

      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.appName}>{content.settings.appName}</h1>

        <div style={styles.dayRow}>
          <span style={styles.dayBadge}>День {progress.currentDay}/7</span>

          <button style={styles.resetButton} onClick={handleResetClick}>
            <span style={styles.resetIcon}>↺</span>
            <span style={styles.resetText}>RESET</span>
          </button>
        </div>

        <h2 style={styles.title}>{currentDayData?.title}</h2>
      </header>

      {/* Навигация по дням */}
      <div style={styles.daysNav}>
        {[1, 2, 3, 4, 5, 6, 7].map(dayNum => {
          const isUnlocked = dayNum <= progress.maxUnlockedDay;
          const isCurrent = dayNum === progress.currentDay;
          const isComplete = isDayComplete(dayNum);

          return (
            <button
              key={dayNum}
              style={{
                ...styles.dayDot,
                ...(isCurrent ? styles.dayDotCurrent : {}),
                ...(isComplete ? styles.dayDotComplete : {}),
                ...(!isUnlocked ? styles.dayDotLocked : {}),
              }}
              onClick={() => handleDaySelect(dayNum)}
              disabled={!isUnlocked}
            >
              {isComplete ? '✓' : dayNum}
            </button>
          );
        })}
      </div>

      {/* Основной контент дня */}
      <DayScreen
        dayData={currentDayData}
        dayProgress={currentDayProgress}
        onL1Increment={() => incrementL1(progress.currentDay)}
        onL2Toggle={() => toggleL2(progress.currentDay)}
        onInjectionRead={() => markInjectionRead(progress.currentDay)}
        isDayComplete={dayComplete}
        hideProgressBar={true}
      />

      {/* Для дней 1-6 показываем финальное сообщение и кнопку перехода */}
      {dayComplete && progress.currentDay < 7 && (
        <div style={styles.nextDayContainer}>
          <div style={styles.completionCard}>
            <span style={styles.completionIcon}>✧</span>
            <p style={styles.completionText}>{finalMessage}</p>
          </div>

          <button
            style={{
              ...styles.nextDayButton,
              ...(nextDayStatus.canGo ? {} : styles.nextDayButtonDisabled),
            }}
            onClick={handleNextDay}
            disabled={!nextDayStatus.canGo}
          >
            {nextDayStatus.canGo
              ? `ПЕРЕЙТИ К ДНЮ ${progress.currentDay + 1}`
              : nextDayStatus.message}
          </button>
        </div>
      )}

      {/* Блок-напоминание про RESET */}
        <div style={styles.resetReminder}>
        <p style={styles.resetReminderText}>
          Если запарился, потерял фокус, заблудился в эмоциях —
          <br />
          жми кнопку
        </p>
        <div style={styles.resetReminderBadge}>
          <span style={styles.resetReminderIcon}>↺</span>
          <span>RESET</span>
        </div>
        <p style={styles.resetReminderText}>
          в верхнем углу экрана.
        </p>
      </div>

      {/* Модальное окно для 7-го дня */}
      <FinalModal
        isOpen={showFinalModal}
        onClose={() => setShowFinalModal(false)}
        completionCount={progress.completionCount}
        daysUntilRestart={daysUntilRestart}
        onRestart={handleRestart}
      />

      {/* Футер */}
      <Footer />

      {/* Оверлей сброса */}
      <ResetOverlay
        isOpen={isResetOverlayOpen}
        onComplete={handleResetComplete}
        onClose={handleResetClose}
        totalResets={progress.totalResets}
      />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: '20px',
    maxWidth: '480px',
    margin: '0 auto',
    position: 'relative',
  },

  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  appName: {
    color: '#E5D2A0',
    fontSize: '13px',
    fontWeight: '500',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    marginBottom: '16px',
    opacity: 0.7,
  },
  dayRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
  },    
  dayBadge: {
    padding: '6px 14px',
    background: 'rgba(229, 210, 160, 0.15)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(229, 210, 160, 0.4)',
    borderRadius: '20px',
    color: '#E5D2A0',
    fontSize: '12px',
    fontWeight: '500',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  resetButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    background: 'rgba(229, 210, 160, 0.15)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#E5D2A0',
    borderRadius: '20px',
    color: '#E5D2A0',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '1px',
    cursor: 'pointer',
  },
  resetIcon: {
    fontSize: '14px',
  },
  resetText: {
    textTransform: 'uppercase',
  },
  title: {
    color: '#FFFFFF',
    fontSize: '22px',
    fontWeight: '600',
    letterSpacing: '-0.3px',
    margin: 0,
  },
  daysNav: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '24px',
  },
  dayDot: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(229, 210, 160, 0.3)',
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayDotCurrent: {
    borderColor: '#E5D2A0',
    color: '#E5D2A0',
    background: 'rgba(229, 210, 160, 0.15)',
  },
  dayDotComplete: {
    borderColor: '#E5D2A0',
    color: '#E5D2A0',
  },
  dayDotLocked: {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
  nextDayContainer: {
    marginTop: '32px',
  },
  completionCard: {
    textAlign: 'center',
    padding: '24px 20px',
    background: 'rgba(229, 210, 160, 0.08)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(229, 210, 160, 0.2)',
    borderRadius: '16px',
    marginBottom: '20px',
  },
  completionIcon: {
    fontSize: '32px',
    color: '#E5D2A0',
    display: 'block',
    marginBottom: '12px',
  },
  completionText: {
    color: '#FFFFFF',
    fontSize: '16px',
    fontWeight: '500',
    margin: 0,
  },
  nextDayButton: {
    width: '100%',
    padding: '16px 32px',
    background: '#E5D2A0',
    border: 'none',
    borderRadius: '30px',
    color: '#0a0a0a',
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '2px',
    cursor: 'pointer',
  },
  nextDayButtonDisabled: {
    background: 'rgba(229, 210, 160, 0.3)',
    color: 'rgba(0, 0, 0, 0.5)',
    cursor: 'not-allowed',
  },
  // Блок-напоминание про RESET
  resetReminder: {
    marginTop: '32px',
    padding: '20px',
    background: 'rgba(229, 210, 160, 0.05)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(229, 210, 160, 0.15)',
    borderRadius: '16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  resetReminderText: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '14px',
    lineHeight: '1.6',
    margin: 0,
  },
    resetReminderBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 18px',
    background: 'rgba(229, 210, 160, 0.15)',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#E5D2A0',
    borderRadius: '20px',
    color: '#E5D2A0',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '1px',
    cursor: 'default',
    pointerEvents: 'none',
  },
  resetReminderIcon: {
    fontSize: '14px',
  },
};

export default App;